// ✅ src/pages/VehicleV2/VehicleSalesorScrapReport.jsx
// FULL UPDATED FILE
//
// ✅ Updated in same pattern as attached code
// ✅ Uses localStorage headers
// ✅ useMemo headers
// ✅ separate loadingInit / loadingList
// ✅ pagination added
// ✅ branch handled from session
// ✅ print uses createSearchParams
// ✅ export csv kept
// ✅ documents navigation kept
//
// Expected backend:
//   GET /api/vehicleSaleScrapReport/branches
//   GET /api/vehicleSaleScrapReport/vehicleTypes
//   GET /api/vehicleSaleScrapReport/list
//   GET /api/vehicleSaleScrapReport/print
//   GET /api/vehicleSaleScrapReport/doc

import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaInfoCircle,
  FaEye,
  FaFileExport,
} from "react-icons/fa";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, createSearchParams } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/vehicleSalesorScrapReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};

const toNum = (v, d = 0) => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};

const formatAmount = (v) => {
  const n = toNum(v, 0);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const exportToCsv = (rows, filename = "vehicle-sale-scrap-report.csv") => {
  if (!rows?.length) {
    toast.info("No data to export");
    return;
  }

  const safeRows = rows.map((r) => {
    const c = { ...r };
    delete c._row;
    return c;
  });

  const headers = Object.keys(safeRows[0]);
  const esc = (val) => {
    const s = String(val ?? "");
    if (s.includes('"') || s.includes(",") || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const csv = [
    headers.join(","),
    ...safeRows.map((r) => headers.map((h) => esc(r[h])).join(",")),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const VehicleSalesorScrapReport = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [showFilters, setShowFilters] = useState(true);

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // dropdown data
  const [branches, setBranches] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);

  // table data
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ backend-matching headers
  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    branchId: sessionBranchType !== "HO" ? sessionBranchId : "Select",
    regNo: "",
    vehicleCode: "",
    vehicleName: "",
    status: "All",
    vehicleTypeId: "Select",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  /* ---------------- dropdown mappers ---------------- */
  const branchOptions = useMemo(() => {
    return (branches || [])
      .map((b) => ({
        value: toStr(
          b.branchId ??
            b.fld_branchid ??
            b.branchid ??
            b.BranchId ??
            b.id ??
            b.value
        ),
        label: toStr(
          b.branchName ??
            b.fld_BranchName ??
            b.fld_branchname ??
            b.branchname ??
            b.BranchName ??
            b.label
        ),
      }))
      .filter((o) => o.value || o.label);
  }, [branches]);

  const vehicleTypeOptions = useMemo(() => {
    return (vehicleTypes || [])
      .map((v) => ({
        value: toStr(
          v.vehicleTypeId ??
            v.fld_vehicletypeid ??
            v.value ??
            v.id
        ),
        label: toStr(
          v.vehicleTypeName ??
            v.fld_vehicletypeindex ??
            v.fld_vehicletypename ??
            v.VehicleTypeName ??
            v.label
        ),
      }))
      .filter((o) => o.label);
  }, [vehicleTypes]);

  /* ---------------- api helpers ---------------- */
  const buildListParams = (f) => ({
    fromDate: toStr(f.fromDate),
    toDate: toStr(f.toDate),
    branchId:
      sessionBranchType !== "HO"
        ? sessionBranchId
        : isSelect(f.branchId)
        ? "Select"
        : toStr(f.branchId),
    regNo: toStr(f.regNo),
    vehicleCode: toStr(f.vehicleCode),
    vehicleName: toStr(f.vehicleName),
    status: toStr(f.status) || "All",
    vehicleTypeId: isSelect(f.vehicleTypeId) ? "Select" : toStr(f.vehicleTypeId),
  });

  /* ---------------- load dropdowns ---------------- */
  const fetchInit = async () => {
    try {
      setLoadingInit(true);

      const [branchRes, vehicleTypeRes] = await Promise.all([
        axios.get(`${API_BASE}/branches`, {
          params: {
            branchType: sessionBranchType || "BRANCH",
            branchId: sessionBranchId || "",
          },
          headers: apiHeaders,
        }),
        axios.get(`${API_BASE}/vehicleTypes`, {
          headers: apiHeaders,
        }),
      ]);

      if (!branchRes.data?.success) {
        throw new Error(branchRes.data?.message || "Failed to load branches");
      }

      if (!vehicleTypeRes.data?.success) {
        throw new Error(vehicleTypeRes.data?.message || "Failed to load vehicle types");
      }

      setBranches(branchRes.data.rows || []);
      setVehicleTypes(vehicleTypeRes.data.rows || []);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to load filters");
    } finally {
      setLoadingInit(false);
    }
  };

  /* ---------------- fetch list ---------------- */
  const fetchList = async () => {
    try {
      setLoadingList(true);

      const params = buildListParams(filters);

      const res = await axios.get(`${API_BASE}/list`, {
        params,
        headers: apiHeaders,
      });

      if (!res.data?.success) {
        throw new Error(res.data?.message || "List fetch failed");
      }

      const fetchedRows = res.data.rows || [];
      setAllRows(fetchedRows);
      setTotal(fetchedRows.length);

      const start = (currentPage - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      setRows(fetchedRows.slice(start, end));
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to load report");
      setRows([]);
      setAllRows([]);
      setTotal(0);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    setRows(allRows.slice(start, end));
  }, [currentPage, allRows]);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- actions ---------------- */
  const handleSearch = async () => {
    setCurrentPage(1);
    try {
      setLoadingList(true);

      const params = buildListParams(filters);

      const res = await axios.get(`${API_BASE}/list`, {
        params,
        headers: apiHeaders,
      });

      if (!res.data?.success) {
        throw new Error(res.data?.message || "Search failed");
      }

      const fetchedRows = res.data.rows || [];
      setAllRows(fetchedRows);
      setTotal(fetchedRows.length);
      setRows(fetchedRows.slice(0, itemsPerPage));

      toast.success(`Found ${res.data?.count ?? fetchedRows.length} records`);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to search report");
      setRows([]);
      setAllRows([]);
      setTotal(0);
    } finally {
      setLoadingList(false);
    }
  };

  const handleViewAll = async () => {
    const reset = {
      fromDate: "",
      toDate: "",
      branchId: sessionBranchType !== "HO" ? sessionBranchId : "Select",
      regNo: "",
      vehicleCode: "",
      vehicleName: "",
      status: "All",
      vehicleTypeId: "Select",
    };

    setFilters(reset);
    setCurrentPage(1);

    try {
      setLoadingList(true);

      const params = buildListParams(reset);

      const res = await axios.get(`${API_BASE}/list`, {
        params,
        headers: apiHeaders,
      });

      if (!res.data?.success) {
        throw new Error(res.data?.message || "View all failed");
      }

      const fetchedRows = res.data.rows || [];
      setAllRows(fetchedRows);
      setTotal(fetchedRows.length);
      setRows(fetchedRows.slice(0, itemsPerPage));

      toast.info("Showing all records");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to load all records");
      setRows([]);
      setAllRows([]);
      setTotal(0);
    } finally {
      setLoadingList(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      branchId: sessionBranchType !== "HO" ? sessionBranchId : "Select",
      regNo: "",
      vehicleCode: "",
      vehicleName: "",
      status: "All",
      vehicleTypeId: "Select",
    });
    toast.info("Filters cleared!");
  };

  const handlePrintReport = () => {
    const query = {
      fromDate: filters.fromDate || "",
      toDate: filters.toDate || "",
      branchId:
        sessionBranchType !== "HO"
          ? sessionBranchId
          : filters.branchId || "Select",
      regNo: filters.regNo || "",
      vehicleCode: filters.vehicleCode || "",
      vehicleName: filters.vehicleName || "",
      status: filters.status || "All",
      vehicleTypeId: filters.vehicleTypeId || "Select",
    };

    navigate({
      pathname: "/vehicle/vehicleSalesandScrapReport_Print",
      search: `?${createSearchParams(query).toString()}`,
    });
  };

  const handleExport = () => {
    exportToCsv(allRows);
  };

  const handleDocuments = (row) => {
    const saleScrapId = row.saleScrapId || 0;
    if (!saleScrapId) {
      toast.info("Document id not available");
      return;
    }

    navigate(
      `/vehicle/VehicleSalesandScrapReport_Doc?saleScrapId=${saleScrapId}`
    );
  };

  /* ---------------- pagination ---------------- */
  const totalPages = Math.ceil(total / itemsPerPage) || 0;
  const indexOfFirstItem = total === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const indexOfLastItem = Math.min(currentPage * itemsPerPage, total);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  /* ---------------- styles ---------------- */
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE SALE / SCRAP REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-4 mb-8">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Sale / Scrap Report
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>

              {(loadingInit || loadingList) && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" +
                      (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" +
                      (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleExport}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaFileExport className="w-4 h-4" />
                Export CSV
              </button>

              <button
                onClick={handlePrintReport}
                className="flex items-center gap-2 bg-black hover:bg-gray-900 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
              >
                <FaPrint />
                Print Report
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branchId"
                    value={filters.branchId}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={sessionBranchType !== "HO"}
                  >
                    <option value="Select">Select</option>
                    {branchOptions.map((b) => (
                      <option key={`${b.value}-${b.label}`} value={b.value}>
                        {b.label || b.value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select
                    name="vehicleTypeId"
                    value={filters.vehicleTypeId}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {vehicleTypeOptions.map((v) => (
                      <option key={`${v.value}-${v.label}`} value={v.value}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Reg.No</label>
                  <input
                    type="text"
                    name="regNo"
                    value={filters.regNo}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter vehicle reg no"
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter vehicle code"
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    name="vehicleName"
                    value={filters.vehicleName}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter vehicle name"
                  />
                </div>

                <div>
                  <label className={labelClass}>Sales Or Scrap</label>
                  <select
                    name="status"
                    value={filters.status}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="All">All</option>
                    <option value="Sales">Sales</option>
                    <option value="scrap">scrap</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 gap-4">
                <button
                  onClick={handleClearFilters}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
                >
                  Clear All Filters
                </button>

                <div className="flex gap-4">
                  <button
                    onClick={handleViewAll}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                    disabled={loadingList}
                  >
                    <FaEye />
                    View All
                  </button>

                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                    disabled={loadingList}
                  >
                    <FaSearch />
                    Search Report
                  </button>
                </div>
              </div>
            </div>
          )}

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  No Records Found
                </h3>
                <p
                  className={`mb-6 max-w-sm mx-auto ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Try adjusting your search criteria or click "View All" to see all records.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">S.No</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Main Type</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Code</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Name</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Reg.No</th>
                      <th className="px-4 py-4 text-center font-semibold">Branch</th>
                      <th className="px-4 py-4 text-center font-semibold">Status</th>
                      <th className="px-4 py-4 text-center font-semibold">Sold Price</th>
                      <th className="px-4 py-4 text-center font-semibold">Reason For Sale/Scrap</th>
                      <th className="px-4 py-4 text-center font-semibold">Recommend By</th>
                      <th className="px-4 py-4 text-center font-semibold">Approved By</th>
                      <th className="px-4 py-4 text-center font-semibold">Scanned Documents</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((item, index) => (
                      <tr
                        key={item.saleScrapId || `${item.vehicleCode}-${index}`}
                        className={`${
                          isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-50"
                        } transition-colors duration-200`}
                      >
                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.sno ?? (indexOfFirstItem + index)}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.mainType ?? ""}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.vehicleCode ?? ""}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.name ?? ""}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.regNo ?? ""}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.branch ?? ""}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.status ?? ""}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {formatAmount(item.price ?? 0)}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.reason ?? ""}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.recommendedBy ?? ""}
                        </td>

                        <td
                          className={`px-4 py-4 text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {item.approvedBy ?? ""}
                        </td>

                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={() => handleDocuments(item)}
                            className="text-blue-500 underline hover:text-blue-700"
                          >
                            Documents
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {total > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
              <div className={`text-sm mb-4 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {indexOfFirstItem} to {indexOfLastItem} of {total} entries
              </div>

              <nav className="flex gap-1">
                <button
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === 1
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Previous
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => paginate(pageNum)}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === pageNum
                          ? `${buttonClass} text-white`
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === totalPages || totalPages === 0
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleSalesorScrapReport;